import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Car } from "lucide-react"

interface VehiclePhotoGalleryProps {
  photos: string[]
  className?: string
  alt?: string
}

const VehiclePhotoGallery = ({ photos, className = "", alt = "Foto do veículo" }: VehiclePhotoGalleryProps) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Reset to first photo when photos array changes
  useEffect(() => {
    setCurrentPhotoIndex(0)
  }, [photos])

  const hasMultiplePhotos = photos.length > 1
  const currentPhoto = photos[currentPhotoIndex]

  const goToNextPhoto = () => {
    if (!hasMultiplePhotos || isTransitioning) return
    
    setIsTransitioning(true)
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length)
    
    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300)
  }

  const goToPrevPhoto = () => {
    if (!hasMultiplePhotos || isTransitioning) return
    
    setIsTransitioning(true)
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length)
    
    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300)
  }

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!hasMultiplePhotos) return
      
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        goToNextPhoto()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goToPrevPhoto()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [hasMultiplePhotos, photos.length, isTransitioning])

  if (photos.length === 0) {
    return (
      <div className={`relative aspect-square w-full overflow-hidden rounded-[1.1rem] bg-muted flex items-center justify-center ${className}`}>
        <div className="flex flex-col items-center text-muted-foreground">
          <Car className="h-12 w-12 mb-2" />
          <span className="text-sm">Sem fotos</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative aspect-square w-full overflow-hidden rounded-[1.1rem] bg-black/5 ${className}`}>
      {/* Photo Display */}
      <div className="w-full h-full relative">
        <img
          src={currentPhoto}
          alt={`${alt} ${currentPhotoIndex + 1} de ${photos.length}`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        />
        
        {/* Photo Counter */}
        {hasMultiplePhotos && (
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {currentPhotoIndex + 1} / {photos.length}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      {hasMultiplePhotos && (
        <>
          {/* Previous Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white h-8 w-8 rounded-full"
            onClick={goToPrevPhoto}
            disabled={isTransitioning}
            aria-label="Foto anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Next Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white h-8 w-8 rounded-full"
            onClick={goToNextPhoto}
            disabled={isTransitioning}
            aria-label="Próxima foto"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}

      {/* Hover Overlay for Navigation Hints */}
      {hasMultiplePhotos && (
        <div className="absolute inset-0 bg-black/0 hover:bg-black/5 transition-colors duration-200 pointer-events-none" />
      )}
    </div>
  )
}

export default VehiclePhotoGallery
